import { getApiClient, getApiToken, SERVICE_URLS } from './authApiClient';

// Underwriting Copilot, backed by CrediArc's Claude service (company-service
// /api/claude/*). A local override is supported only outside production. This
// prevents a locally-built production bundle from asking customers for access
// to services or devices on their own network.
export const resolveClaudeApiBaseUrl = ({
  nodeEnv = process.env.NODE_ENV,
  override = process.env.REACT_APP_CLAUDE_API,
  companyUrl = SERVICE_URLS.company,
} = {}) => (
  nodeEnv === 'production' ? companyUrl : (override || companyUrl)
);

const claudeBaseUrl = () => resolveClaudeApiBaseUrl();
const claudeConfig = () => {
  const baseURL = resolveClaudeApiBaseUrl();
  return baseURL && baseURL !== SERVICE_URLS.company ? { baseURL } : {};
};

// Natural-language BI reporting query -> structured report spec (admin only).
export const nlReportQuery = async (question) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post('/claude/reporting/nl-query', { question });
  return response.data?.data?.spec || null;
};

// payload: { tenantName, entityName, entity, messages, allowWeb }
export const sendClaudeChat = async (payload) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post('/claude/chat', payload, claudeConfig());
    return response.data;
  } catch (error) {
    console.error('Copilot chat error:', error?.response?.status, error?.response?.data || error?.message);
    throw error;
  }
};

export const startClaudeChatJob = async (payload) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post('/claude/chat/start', payload, claudeConfig());
  return response.data?.data;
};

export const getClaudeChatJob = async (jobId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/claude/chat/status/${jobId}`, claudeConfig());
  return response.data?.data;
};

// Deep portfolio questions can exceed the production load balancer's request
// window. The analysis runs server-side while the browser makes short polls.
export const runClaudeChatJob = async (
  payload,
  { signal, pollIntervalMs = 1500, timeoutMs = 10 * 60 * 1000, onStatus } = {}
) => {
  const started = await startClaudeChatJob(payload);
  if (!started?.jobId) throw new Error('ArcAI analysis did not return a job id');
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const job = await getClaudeChatJob(started.jobId);
    if (onStatus) onStatus(job);
    if (job?.status === 'done') return { reply: job.reply || '', stopReason: job.stopReason };
    if (job?.status === 'error') throw new Error(job.message || 'ArcAI analysis failed');
    await new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, pollIntervalMs);
      if (signal) {
        signal.addEventListener('abort', () => {
          clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        }, { once: true });
      }
    });
  }
  throw new Error('ArcAI analysis timed out. Retry the request; no portfolio data was changed.');
};

// Streaming variant. Calls onDelta(fullTextSoFar) as tokens arrive and resolves
// with { reply, stopReason }. Throws (with .status when available) on failure so
// the caller can fall back to sendClaudeChat.
export const streamClaudeChat = async (payload, { onDelta, signal } = {}) => {
  const token = getApiToken();
  const res = await fetch(`${claudeBaseUrl()}/claude/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok || !res.body) {
    let message;
    try {
      message = (await res.json())?.message;
    } catch (e) {
      /* non-JSON error body */
    }
    const err = new Error(message || `Copilot stream failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';
  let stopReason;

  const handleEvent = (data) => {
    if (data.type === 'delta') {
      full += data.delta;
      if (onDelta) onDelta(full);
    } else if (data.type === 'done') {
      if (data.reply) full = data.reply;
      stopReason = data.stopReason;
    } else if (data.type === 'error') {
      const err = new Error(data.message || 'Copilot stream error');
      err.streamError = true;
      throw err;
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() || '';
    for (const chunk of chunks) {
      const line = chunk.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const json = line.slice(5).trim();
      if (!json) continue;
      handleEvent(JSON.parse(json));
    }
  }

  return { reply: full, stopReason };
};

// AI Assessment Review — start-job + poll (robust behind the prod load balancer,
// which resets long streaming responses). Short requests only.
export const startAssessmentReview = async (payload) => {
  const apiClient = getApiClient('company');
  const res = await apiClient.post('/claude/review/start', payload, claudeConfig());
  return res.data?.data; // { jobId, checklist }
};

export const getReviewStatus = async (jobId) => {
  const apiClient = getApiClient('company');
  const res = await apiClient.get(`/claude/review/status/${jobId}`, claudeConfig());
  return res.data?.data; // { status: 'running'|'done'|'error', report?, message? }
};

// Push Claude's assessment (ARC score, recommended limit, alternative-data report) onto
// the company record. payload: { tenantId, companyId, arcScore, recommendedLimit,
// underwritingReason, alternativeDataReport }
export const pushReviewAssessment = async (payload) => {
  const apiClient = getApiClient('company');
  const res = await apiClient.post('/claude/review/push', payload, claudeConfig());
  return res.data?.data;
};

// Legacy SSE variant (kept for local dev). Streams SSE events to onEvent(event).
export const streamAssessmentReview = async (payload, { onEvent, signal } = {}) => {
  const token = getApiToken();
  const res = await fetch(`${claudeBaseUrl()}/claude/review/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok || !res.body) {
    let message;
    try {
      message = (await res.json())?.message;
    } catch (e) {
      /* non-JSON error body */
    }
    const err = new Error(message || `Review failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let report = null;

  const handle = (data) => {
    if (onEvent) onEvent(data);
    if (data.type === 'report') report = data.report;
    if (data.type === 'error') {
      const err = new Error(data.message || 'Review error');
      err.streamError = true;
      throw err;
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() || '';
    for (const chunk of chunks) {
      const line = chunk.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const json = line.slice(5).trim();
      if (!json) continue;
      handle(JSON.parse(json));
    }
  }

  if (!report) throw new Error('Review did not return a report');
  return report;
};
